import { IconButton, IconButtonProps } from 'omakase';
import { IconCrossLarge, IconSearch } from '@tock/icons';
import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
  OmakasePolymorphicRootComponent,
} from 'omakase/utils/OmakasePolymorphicComponent';
import { Theme } from '@emotion/react';
import { mergeRefs } from 'omakase/utils/mergeRefs';
import {
  searchInputBaseStyles,
  searchInputClearButtonStyles,
  searchInputInputStyles,
  searchInputSearchIconStyles,
} from './searchInputCssDefs';
import React, { useRef, useState } from 'react';

type IconButtonPropsOverride = Pick<IconButtonProps, 'onClick'>;

export type SearchInputSize = 'medium' | 'large' | 'xlarge';
export type SearchInputVarant = 'basic' | 'ghost';

export interface SearchInputProps {
  /**
   * The props to be passed to the inner IconButton
   */
  IconButtonProps?: IconButtonPropsOverride;
  /**
   * An optional disabled value
   */
  disabled?: boolean;
  /**
   * full width block style
   */
  fullWidth?: boolean;
  /**
   * the change event
   */
  onChange?(value: string): void;
  /**
   * the input event
   */
  onInput?(value: string): void;
  /**
   * A required starting string value for the input
   */
  placeholder: string;
  /**
   * sizes
   */
  size?: SearchInputSize;
  /**
   * The design variant
   */
  variant?: SearchInputVarant;
  /**
   * The value for the input
   */
  value?: string;
  /**
   * Runs when the "clear button" is pressed. If present, the clear button will always be shown, even if there is no input
   */
  onClear?(): void;
}

export const SearchInputDefaultElement = 'input';
type SearchInputTypeGuard = 'input';

const SearchInput: OmakasePolymorphicComponent<
  SearchInputProps,
  typeof SearchInputDefaultElement,
  SearchInputTypeGuard
> = React.forwardRef(
  <E extends React.ElementType = typeof SearchInputDefaultElement>(
    {
      component,
      disabled = false,
      fullWidth = false,
      onChange,
      onInput,
      placeholder,
      size = 'medium',
      variant = 'basic',
      IconButtonProps,
      value,
      onClear,
      ...rest
    }: OmakasePolymorphicComponentProps<SearchInputProps, E, SearchInputTypeGuard>,
    ref: React.Ref<HTMLInputElement> | undefined
  ) => {
    const Component: React.ElementType = component || SearchInputDefaultElement;

    const [inputIsEmpty, setInputIsEmpty] = useState(!value);
    const inputRef = useRef<HTMLInputElement>(null);
    const currentValue = () => inputRef.current?.value || '';

    const clearSearch = () => {
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.focus();
      }
      handleChange();
      handleInput();

      IconButtonProps?.onClick?.();
    };

    const handleChange = () => {
      onChange?.(currentValue());
    };

    const handleInput = () => {
      const value = currentValue();
      setInputIsEmpty(!value);
      onInput?.(value);
    };

    const hideClearButton = inputIsEmpty && !onClear;

    return (
      <div css={(theme: Theme) => searchInputBaseStyles(theme, fullWidth)}>
        <OmakasePolymorphicRootComponent
          css={(theme: Theme) => searchInputInputStyles(theme, size, variant, disabled)}
          disabled={disabled}
          onChange={handleChange}
          onInput={handleInput}
          placeholder={placeholder}
          ref={mergeRefs(ref ? [inputRef, ref] : [inputRef])}
          type="search"
          component={Component}
          value={value}
          {...rest}
        />
        <IconSearch
          css={(theme: Theme) => searchInputSearchIconStyles(theme, disabled, inputIsEmpty)}
          fontSize="small"
        />

        {hideClearButton ? null : (
          <IconButton
            aria-label="Clear search"
            css={searchInputClearButtonStyles}
            disabled={disabled}
            onClick={() => {
              clearSearch();
              onClear?.();
            }}
            size="xsmall"
            variant="ghost"
          >
            <IconCrossLarge />
          </IconButton>
        )}
      </div>
    );
  }
);

export default SearchInput;
